import { CanvasProps, CanvasWithScreenshotting, useSetThreeKey } from '@k3/core'
import { ButtonGroup } from '@mui/material'
import { useContextBridge } from '@react-three/drei'
import { RootState } from '@react-three/fiber'
import React, { useEffect, useRef } from 'react'
import { Suspense } from 'react'
import { ThreeCamera } from '../../classes/three'
import { SuspenseLoader } from '../../components/suspense'
import {
  MontageContext,
  MontageUpdateContext,
  useMontage,
  useMontageUpdate,
} from '../../context/montage.context'
import { useInterface } from '../../hooks/interface.hook'
import { Layout } from '../../three/layout'
import { TerraceCoverMesh } from '../../three/mesh/terraceCover'
import { Ruler } from '../mesh/terraceCover/ruler/Ruler'
import { rulers } from '../mesh/terraceCover/ruler/rulers'
import { SceneButtonExtended } from '../../components/scene/SceneButtonExtended'
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak'
import CropFreeIcon from '@mui/icons-material/CropFree'
import SquareFootIcon from '@mui/icons-material/SquareFoot'
import ModeNightIcon from '@mui/icons-material/ModeNight'
import LightModeIcon from '@mui/icons-material/LightMode'
import { BloomPass } from '../utility/light/BloomPass'

const canvasProps: Omit<CanvasProps, 'children'> = {
  className: 'canvas',
  shadows: true,
  camera: {
    position: ThreeCamera.startPosition,
    fov: ThreeCamera.cameraFov,
    far: 1000,
  },
  onCreated: (e: RootState) => {
    ;(window as any).oc3 = e
  },
}

export const MontageRoot = (): JSX.Element => {
  useInterface()
  // Sets a key for AR in store
  useSetThreeKey()
  

  const ContextBridge = useContextBridge(MontageContext, MontageUpdateContext)
  const [alignment, setAlignment] = React.useState(true)
  const { onHandleEgo, onHandleNight} = useMontageUpdate()
  const {ego, night} = useMontage()
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    onHandleNight(params.get('view') === 'night' ? true : false)
    onHandleEgo(params.get('mode') === 'ego' ? true : false)
  }, [])

  return (
    <Suspense fallback={<SuspenseLoader />}>
      <ButtonGroup
        variant="text"
        color="primary"
        aria-label=""
        style={{
          position: 'absolute',
          zIndex: 2,
          right: 0,
          marginTop: '115px',
          marginRight: '25px',
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <SceneButtonExtended
          value={alignment}
          defaultValue={true}
          label="Maßlinien An/Aus"
          id={"masslinien"}
          onChange={() => setAlignment(!alignment)}
        >
          <SquareFootIcon />
        </SceneButtonExtended>
        <SceneButtonExtended
          value={ego?.ego}
          defaultValue={false}
          label="Perspektive ändern"
          id={"cameraEgo"}
          onChange={() => onHandleEgo(!ego?.ego)}
        >
          {ego?.ego ? <CropFreeIcon /> : <CenterFocusWeakIcon />}
        </SceneButtonExtended>
        <SceneButtonExtended
          value={night?.night}
          defaultValue={night?.night}
          label="Tag/Nacht Modus"
          id={"night"}
          onChange={() => onHandleNight(!night?.night)}
        >
          {night?.night ? (
            <LightModeIcon
              sx={{
                color: `yellow`,
              }}
            />
          ) : (
            <ModeNightIcon
              sx={{
                color: `black`,
              }}
            />
          )}
        </SceneButtonExtended>
      </ButtonGroup>

      <CanvasWithScreenshotting canvasProps={canvasProps} >
        <Suspense fallback={null}>
          {rulers().map(
            (rule, id) => alignment && <Ruler rule={rule} key={id} />
          )}
        </Suspense>
        <ContextBridge>
          <Layout>
            <TerraceCoverMesh />
          </Layout>
        </ContextBridge>
        {night?.night && <BloomPass />}
      </CanvasWithScreenshotting>
  
    </Suspense>
  )
}
